// Utils
import { isClone } from '../../../common/elements/utils/elementTypeUtils';
import {
    getClonedElementType,
    getElementId,
    getElementType,
} from '../../../common/elements/utils/elementPropertyUtils';

// Constants
import { LINK_TYPE } from '../../../common/links/linkConstants';
import { CAPTION_EDITOR_KEY } from '../../components/caption/captionConstants';
import { COLOR_SWATCH_EDITOR_KEY, COLOR_SWATCH_TYPE } from '../../../common/colorSwatches/colorSwatchesConstants';
import { DOCUMENT_TITLE_EDITOR_KEY, DOCUMENT_TYPE } from '../../../common/documents/documentConstants';
import { TABLE_TITLE_EDITOR_KEY, TABLE_TYPE } from '../../../common/table/tableConstants';

/**
 * Adds a suffix to the editor string (either ID or key) based on the context provided, if necessary.
 */
export const addEditorContextSuffix = ({ isFocusedForegroundElement, isModalView }, editorStr) => {
    if (isFocusedForegroundElement) return `${editorStr}-FOCUS`;
    if (isModalView) return `${editorStr}-MODAL`;
    return editorStr;
};

/**
 * Gets the main editor ID based on the element ID and type.
 */
const getMainEditorIdForElement = (element) => `${getElementId(element)}-${getElementType(element)}`;

/**
 * Gets the main editor ID based on the element ID, type and context;
 */
export const getMainEditorId = (args) => {
    const { element } = args;
    return addEditorContextSuffix(args, getMainEditorIdForElement(element));
};

/**
 * Determines the main editor key, based on the element type.
 */
const getMainEditorKeyForElementType = (element) => {
    let elementType = getElementType(element);

    if (isClone(element)) {
        elementType = getClonedElementType(element);
    }

    switch (elementType) {
        case LINK_TYPE:
            return 'LINK_TITLE';
        case COLOR_SWATCH_TYPE:
            return COLOR_SWATCH_EDITOR_KEY;
        case DOCUMENT_TYPE:
            return DOCUMENT_TITLE_EDITOR_KEY;
        case TABLE_TYPE:
            return TABLE_TITLE_EDITOR_KEY;
        default:
            return getElementType(element);
    }
};

/**
 * Determines the main editor key, based on the element type and context of the element.
 */
export const getMainEditorKey = (args) => {
    const { element } = args;
    return addEditorContextSuffix(args, getMainEditorKeyForElementType(element));
};

/**
 * Gets an editor ID for an element's caption.
 */
export const getCaptionEditorId = (args) => {
    const { element } = args;
    const captionEditorId = `${getElementId(element)}-${CAPTION_EDITOR_KEY}`;
    return addEditorContextSuffix(args, captionEditorId);
};

/**
 * Gets an editor key for an element's caption.
 */
export const getCaptionEditorKey = (args) => addEditorContextSuffix(args, CAPTION_EDITOR_KEY);
